import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, switchMap, takeUntil } from 'rxjs';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  FormFieldConfig,
  SearchFormComponent,
} from '@shared/components/search-form/search-form.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  ConfirmationButton,
  ConfirmationDialogComponent,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { PermissionService } from '@app/core/services/permission.service';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { extractErrorMessage } from '@app/shared/utils/error-handler.util';
import { formatCalendarDate } from './utils/tax-category-date.util';
import {
  TaxCategory,
  TaxCategorySearchParams,
  TaxCategoryService,
} from './services/tax-category.service';
import {
  TAX_CATEGORY_ACTION_COLUMN,
  TAX_CATEGORY_COLUMNS,
  TAX_CATEGORY_DEFAULT_COLUMNS_ORDER,
  TAX_CATEGORY_FLEXIBLE_COLUMNS_ORDER,
  buildTaxCategorySearchConfig,
} from './tax-category.config';
import {
  TaxCategoryDialogComponent,
  TaxCategoryDialogResult,
} from './components/tax-category-dialog/tax-category-dialog.component';

@Component({
  selector: 'app-tax-category',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    TagModule,
    ToastModule,
    AppButtonComponent,
    AdvancedDataTableComponent,
    SearchFormComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService, DialogService],
  templateUrl: './tax-category.component.html',
  styleUrl: './tax-category.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxCategoryComponent implements OnInit, OnDestroy {
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  private readonly destroy$ = new Subject<void>();
  private readonly taxCategoryService = inject(TaxCategoryService);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly dialogService = inject(DialogService);
  private readonly permissionService = inject(PermissionService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  private dialogRef: DynamicDialogRef | null = null;
  private selectedStatusTarget: TaxCategory | null = null;
  private selectedStatusValue = false;

  taxCategories = signal<TaxCategory[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  rows = 20;
  page = signal(0);
  searchParams = signal<TaxCategorySearchParams>({});

  showConfirmationDialog = signal(false);
  confirmationTitle = signal('');
  confirmationMessage = signal('');
  confirmationButtons = signal<ConfirmationButton[]>([]);

  readonly searchFormConfig: FormFieldConfig[] = buildTaxCategorySearchConfig();

  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.product.tax_categories.view',
    );
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.product.tax_categories.create',
    );
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.product.tax_categories.edit',
    );
  });
  hasAnyActionPermission = computed(() => this.canRead() || this.canUpdate());

  defaultColumns = computed((): TableColumn[] => {
    const columns: TableColumn[] = [];
    for (const prop of TAX_CATEGORY_DEFAULT_COLUMNS_ORDER) {
      const column = TAX_CATEGORY_COLUMNS.find((item) => item.prop === prop);
      if (column) {
        columns.push(column);
      }
    }

    if (this.hasAnyActionPermission()) {
      const actionColumn = TAX_CATEGORY_COLUMNS.find(
        (item) => item.prop === TAX_CATEGORY_ACTION_COLUMN.ACTIONS,
      );
      if (actionColumn) {
        columns.push(actionColumn);
      }
    }

    return columns;
  });

  flexibleColumns = computed((): TableColumn[] =>
    TAX_CATEGORY_FLEXIBLE_COLUMNS_ORDER.map((prop) =>
      TAX_CATEGORY_COLUMNS.find((item) => item.prop === prop),
    ).filter((item): item is TableColumn => !!item),
  );

  ngOnInit(): void {
    this.loadTaxCategories();
  }

  ngOnDestroy(): void {
    this.dialogRef?.close();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(searchData: any): void {
    this.searchParams.set({
      taxCode: searchData.taxCode || undefined,
      taxName: searchData.taxName || undefined,
      activeFlag: searchData.activeFlag ?? null,
      effectiveAt: searchData.effectiveAt || undefined,
    });
    this.page.set(0);
    this.loadTaxCategories();
  }

  onReset(): void {
    this.searchParams.set({});
    this.page.set(0);
    this.loadTaxCategories();
  }

  onPageChange(event: any): void {
    this.page.set(event.page);
    this.rows = event.rows;
    this.loadTaxCategories();
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  onAdd(): void {
    if (!this.canCreate()) {
      this.showNoPermission();
      return;
    }

    this.openDialog('create');
  }

  onView(taxCategory: TaxCategory): void {
    if (!this.canRead()) {
      this.showNoPermission();
      return;
    }

    this.openDialog('view', taxCategory);
  }

  onEdit(taxCategory: TaxCategory): void {
    if (!this.canUpdate()) {
      this.showNoPermission();
      return;
    }

    this.openDialog('edit', taxCategory);
  }

  onToggleActive(taxCategory: TaxCategory): void {
    if (!this.canUpdate()) {
      this.showNoPermission();
      return;
    }

    this.selectedStatusTarget = taxCategory;
    this.selectedStatusValue = !taxCategory.activeFlag;
    const actionKey = taxCategory.activeFlag ? 'deactivate' : 'activate';

    this.confirmationTitle.set(
      this.translateService.instant(
        `settings.product.tax_categories.actions.${actionKey}`,
      ),
    );
    this.confirmationMessage.set(
      this.translateService.instant(
        `settings.product.tax_categories.messages.confirm_${actionKey}`,
        { code: taxCategory.taxCode },
      ),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.confirm'),
        action: 'confirm',
        severity: taxCategory.activeFlag ? 'danger' : 'success',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }

  onConfirmationDialogAction(action: string): void {
    if (action !== 'confirm' || !this.selectedStatusTarget) {
      this.resetConfirmation();
      return;
    }

    this.taxCategoryService
      .updateActiveFlag(this.selectedStatusTarget.id, this.selectedStatusValue)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadTaxCategories();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'settings.product.tax_categories.messages.status_success',
            ),
          });
          this.resetConfirmation();
        },
        error: (error: any) => {
          this.showError(error, 'settings.product.tax_categories.messages.status_error');
          this.resetConfirmation();
        },
      });
  }

  formatDate(value?: string | null, exclusiveEnd = false): string {
    return formatCalendarDate(value, exclusiveEnd);
  }

  formatRate(value?: number | null): string {
    return value === null || value === undefined ? '-' : `${value}%`;
  }

  getStatusLabel(activeFlag: boolean): string {
    return this.translateService.instant(
      activeFlag
        ? 'settings.product.tax_categories.status.active'
        : 'settings.product.tax_categories.status.inactive',
    );
  }

  getStatusSeverity(activeFlag: boolean): string {
    return activeFlag ? 'success' : 'danger';
  }

  private loadTaxCategories(): void {
    this.loading.set(true);
    this.taxCategoryService
      .search({
        ...this.searchParams(),
        page: this.page(),
        size: this.rows,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.taxCategories.set(response.items);
          this.totalRecords.set(response.totalRecords);
          this.loading.set(false);
        },
        error: (error: any) => {
          this.loading.set(false);
          this.showError(error, 'settings.product.tax_categories.messages.load_error');
        },
      });
  }

  private openDialog(
    mode: 'create' | 'edit' | 'view',
    taxCategory?: TaxCategory,
  ): void {
    this.dialogRef = this.dialogService.open(TaxCategoryDialogComponent, {
      header: this.translateService.instant(
        `settings.product.tax_categories.dialog.${mode}_title`,
      ),
      width: '680px',
      modal: true,
      closable: true,
      data: { mode, taxCategory },
    });

    this.dialogRef.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((result?: TaxCategoryDialogResult) => {
        if (!result || mode === 'view') {
          return;
        }

        const request$ =
          mode === 'edit' && taxCategory
            ? this.taxCategoryService.update(taxCategory.id, result.payload)
            : this.taxCategoryService.create(result.payload);

        request$
          .pipe(
            switchMap(() =>
              this.taxCategoryService.search({
                ...this.searchParams(),
                page: this.page(),
                size: this.rows,
              }),
            ),
            takeUntil(this.destroy$),
          )
          .subscribe({
            next: (response) => {
              this.taxCategories.set(response.items);
              this.totalRecords.set(response.totalRecords);
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant(
                  'common.messages.success',
                ),
                detail: this.translateService.instant(
                  mode === 'edit'
                    ? 'settings.product.tax_categories.messages.update_success'
                    : 'settings.product.tax_categories.messages.create_success',
                ),
              });
            },
            error: (error: any) =>
              this.showError(
                error,
                'settings.product.tax_categories.messages.save_error',
              ),
          });
      });
  }

  private showNoPermission(): void {
    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.instant('common.permissions.no_permission'),
      detail: this.translateService.instant('common.permissions.no_permission'),
    });
  }

  private showError(error: any, fallbackKey: string): void {
    const errorMessage = extractErrorMessage(error);
    this.errorDialogService.showError(
      this.errorTranslatorService.translate(errorMessage, fallbackKey),
    );
  }

  private resetConfirmation(): void {
    this.showConfirmationDialog.set(false);
    this.selectedStatusTarget = null;
  }
}
